import { HotelsSearchInput } from '@shared/components/HotelsSearchInput';
import { LocationInputV2 } from '@shared/components/LocationInputV2';
import type { LocationInputV2Response } from '@shared/components/LocationInputV2/LocationInputV2.types';
import { withWindow } from '@shared/utils/withWindow';
import { FormControl, Box, styled } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';
import { useField } from 'formik';
import React from 'react';
import { useTranslation } from '@shared/core';
import * as Yup from 'yup';
import { useSearchFormConfig } from '../useSearchFormConfig';

const LOCATION_INPUT_FONT_SIZE = 13;
const StyledLocationContainer = styled(Box)`
  div[aria-haspopup='listbox'] {
    border-radius: ${pxToRem(16)};
  }

  div[aria-haspopup='listbox'] input {
    font-size: ${pxToRem(LOCATION_INPUT_FONT_SIZE)} !important;
    text-overflow: ellipsis;
    padding-left: ${pxToRem(48)} !important;
    margin-top: ${pxToRem(4)} !important;
  }

  div[aria-haspopup='listbox'] input:focus,
  div[aria-haspopup='listbox'] input:active {
    padding-left: ${pxToRem(48)} !important;
    margin-top: ${pxToRem(4)} !important;
  }

  div[aria-haspopup='listbox'] input::placeholder {
    font-size: ${pxToRem(LOCATION_INPUT_FONT_SIZE)};
  }

  /* Ensure icon stays in place */
  div[aria-haspopup='listbox'] > svg {
    position: absolute !important;
    top: 50% !important;
    transform: translateY(-50%) !important;
    pointer-events: none !important;
  }
`;

const searchInputOverrides = {
  Root: {
    props: {
      maxWidth: '100%',
    },
  },
  Button: {
    props: {
      backgroundColor: 'transparent',
      color: 'buttonOutlineNeutralText',
      _hover: {
        backgroundColor: 'transparent',
        color: 'buttonOutlineNeutralText',
      },
      _active: {
        backgroundColor: 'transparent',
        color: 'buttonOutlineNeutralText',
      },
    },
  },
};

const schema = Yup.object({
  displayName: Yup.string(),
  latitude: Yup.number(),
  longitude: Yup.number(),
  googlePlaceId: Yup.string(),
}).test('location', 'Invalid location', function (value) {
  if (!value) {
    return false;
  }
  return value.displayName && value.latitude && value.longitude;
});
type ISchema = {
  displayName: string;
  latitude: number;
  longitude: number;
  googlePlaceId: string;
};

const initialValue = {
  displayName: '',
  googlePlaceId: '',
  latitude: undefined,
  longitude: undefined,
};

const Fields = ({ fieldsId }: { fieldsId: string }) => {
  const { tq } = useTranslation('accommodationsConsole');
  // avoid passing value because I want LocationInputV2 to be an uncontrolled component, we just want the location info
  const [{ value, ...restField }, meta, helper] = useField<Partial<ISchema>>(fieldsId);
  const { isOpenedFormView, isCompactVersion } = useSearchFormConfig();
  const onChange = (response: LocationInputV2Response | null) => {
    if (!response) {
      // Reset formik state so stale coordinates don't pass validation on clear.
      helper.setValue({ displayName: '', latitude: undefined, longitude: undefined, googlePlaceId: '' });
      return;
    }
    helper.setValue({
      displayName: response.displayName,
      latitude: response.lat,
      longitude: response.lng,
      googlePlaceId: response.placeId,
    });
    // This code is added to lose focus, especially on mobile, after searching for results.
    withWindow(() => {
      if (document.activeElement) {
        const activeElement = document.activeElement as HTMLElement;

        activeElement?.blur();
      }
    });
  };
  // Yup's object-level .test() produces an object error, not a string;
  // coerce so FormControl doesn't try to render an object as a React child.
  const errorMessage = meta.touched ? (typeof meta.error === 'string' ? meta.error : meta.error ? tq`Invalid location` : undefined) : undefined;

  return (
    <FormControl
      {...meta}
      zIndex="banner"
      width="100%"
      maxWidth={isCompactVersion ? 'full' : { _: 'full', md: pxToRem(260) }}
      position={isCompactVersion ? 'static' : { _: 'static', md: 'absolute' }}
      top={0}
      isInvalid={!!errorMessage}
      error={errorMessage}
    >
      {isOpenedFormView ? (
        <HotelsSearchInput
          {...restField}
          handleOnSearch={res =>
            onChange({
              placeId: res?.location?.placeId || '',
              displayName: res?.location?.detailedPlace || '',
              lat: res?.location?.lat || 0,
              lng: res?.location?.lng || 0,
            })
          }
          handleOnChangeLocation={res =>
            onChange({
              placeId: res?.location?.placeId || '',
              displayName: res?.location?.detailedPlace || '',
              lat: res?.location?.lat || 0,
              lng: res?.location?.lng || 0,
            })
          }
          hideIcon
          asForm={false}
          overrides={searchInputOverrides}
          eventLocationData={{ place: value.displayName || '', lat: value.latitude, lng: value.longitude, placeId: '' }}
        />
      ) : (
        <StyledLocationContainer>
          <LocationInputV2
            {...restField}
            includeMapIcon
            iconRight={null}
            noBorder
            placeholder={tq`Center Park Boathouse`}
            placePredictionTypes={[]}
            // Free text resolves to no coordinates, which this field's schema rejects — only offer real predictions.
            allowCustomPlace={false}
            isHideListItemIcon={true}
            defaultValue={value.displayName}
            onLocationChange={onChange}
            includeHotelIcon={false}
          />
        </StyledLocationContainer>
      )}
    </FormControl>
  );
};

export { Fields, schema, initialValue };
